import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';

const {
  Box,
  Stack,
  HorizontalStack,
  Divider,
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionContent,
  TextSkeleton,
  SkeletonFill,
} = designSystem;

const DEFAULT_SECTIONS_COUNT = 6;
const CAPITALIZATION_OPTIONS_COUNT = 4;

const ROW_PADDING = {
  paddingTop: 'spacing-xs',
  paddingBottom: 'spacing-xs',
  paddingLeft: 'spacing-m',
  paddingRight: 'spacing-m',
};

const SkeletonField = ({ short = false }) => (
  <div
    className={classNames('ws-design-fonts-dialog__skeleton-field', {
      'ws-design-fonts-dialog__skeleton-field--short': short,
    })}
  >
    <SkeletonFill />
  </div>
);

SkeletonField.propTypes = {
  short: PropTypes.bool,
};

// `TextSkeleton` only sets `max-width`, so as a flex child it collapses to its content
// width. Wrapping it in a block with an explicit `width` (and `flex-shrink: 0`) gives it a
// definite size and lets the skeleton line fill it.
const SkeletonBar = ({ width, contrasted = false }) => (
  <div
    className={classNames('ws-design-fonts-dialog__skeleton-bar', {
      'ws-design-fonts-dialog__skeleton-bar--contrasted': contrasted,
    })}
    style={{ width }}
  >
    <TextSkeleton width="100%" />
  </div>
);

SkeletonBar.propTypes = {
  width: PropTypes.string.isRequired,
  contrasted: PropTypes.bool,
};

// Label on the left, an input-like field filling the rest (Font, Weight, Size/Height).
const SkeletonControl = ({ children }) => (
  <HorizontalStack gap="spacing-m" verticalAlign="center">
    <SkeletonBar width="56px" />
    {children}
  </HorizontalStack>
);

SkeletonControl.propTypes = {
  children: PropTypes.node.isRequired,
};

// Label on the left, a control aligned to the right (Color, Capitalization, Letter spacing).
const SkeletonRow = ({ labelWidth, children }) => (
  <HorizontalStack align="space-between" verticalAlign="center">
    <SkeletonBar width={labelWidth} />
    {children}
  </HorizontalStack>
);

SkeletonRow.propTypes = {
  labelWidth: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const SkeletonItemContent = () => (
  <React.Fragment>
    <Box
      paddingTop="spacing-xs"
      paddingBottom="spacing-s"
      paddingLeft="spacing-m"
      paddingRight="spacing-m"
    >
      <Stack gap="spacing-xs">
        <SkeletonControl>
          <SkeletonField />
        </SkeletonControl>
        <SkeletonControl>
          <SkeletonField />
        </SkeletonControl>
        <SkeletonControl>
          <SkeletonField />
        </SkeletonControl>
      </Stack>
    </Box>

    <Divider appearance="light-grey" />

    <Box {...ROW_PADDING}>
      <SkeletonRow labelWidth="48px">
        <SkeletonBar width="104px" />
      </SkeletonRow>
    </Box>

    <Divider appearance="light-grey" />

    <Box {...ROW_PADDING}>
      <SkeletonRow labelWidth="96px">
        <HorizontalStack gap="spacing-2-xs">
          {[...Array(CAPITALIZATION_OPTIONS_COUNT).keys()].map((option) => (
            <div key={option} className="ws-design-fonts-dialog__skeleton-square">
              <SkeletonFill />
            </div>
          ))}
        </HorizontalStack>
      </SkeletonRow>
    </Box>

    <Divider appearance="light-grey" />

    <Box {...ROW_PADDING}>
      <SkeletonRow labelWidth="88px">
        <SkeletonField short />
      </SkeletonRow>
    </Box>
  </React.Fragment>
);

const FontsSettingsSkeleton = ({ sectionsCount = DEFAULT_SECTIONS_COUNT }) => {
  const sections = [...Array(sectionsCount).keys()];

  return (
    <Accordion appearance="grey" defaultActiveItem={`skeleton-${sections[0]}`}>
      {sections.map((index) => (
        <AccordionGroup key={`skeleton-${index}`} itemId={`skeleton-${index}`}>
          <AccordionHeader title={<SkeletonBar width="120px" contrasted />} isSticky />
          <AccordionContent>
            <SkeletonItemContent />
          </AccordionContent>
        </AccordionGroup>
      ))}
    </Accordion>
  );
};

FontsSettingsSkeleton.propTypes = {
  sectionsCount: PropTypes.number,
};

export default FontsSettingsSkeleton;
